import React, { useMemo, useState } from "react";
import {
    Alert,
    Button,
    Card,
    Space,
    Table,
    Tag,
    Upload,
    message,
} from "antd";
import { DeleteOutlined, InboxOutlined, UploadOutlined } from "@ant-design/icons";
import PdfUploadField from "./PdfUploadField";
import {
    previewCustomerTransactionExcel,
    importCustomerTransactionExcel,
} from "../../api/customerTransactionApi";

const { Dragger } = Upload;

export default function CustomerTransactionExcelImport() {
    const [excelFile, setExcelFile] = useState(null);
    const [previewRows, setPreviewRows] = useState([]);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handlePreview = async (file) => {
        try {
            setLoadingPreview(true);
            setExcelFile(file);

            const result = await previewCustomerTransactionExcel(file);

            const rows = (result?.rows || []).map((row, index) => ({
                ...row,
                key: `${row.requestNumber || index}-${row.rowNumber}`,
                invoiceFile: null,
                contractFile: null,
            }));

            setPreviewRows(rows);
            message.success("Excel preview loaded");
        } catch (error) {
            message.error(error.message || "Failed to preview Excel");
        } finally {
            setLoadingPreview(false);
        }
    };

    const updateRowFile = (key, field, file) => {
        setPreviewRows((prev) =>
            prev.map((row) =>
                row.key === key ? { ...row, [field]: file } : row
            )
        );
    };

    const allRowsReady = useMemo(() => {
        return previewRows.length > 0 &&
            previewRows.every((row) => row.invoiceFile && row.contractFile);
    }, [previewRows]);

    const handleSubmit = async () => {
        if (!allRowsReady) {
            message.error("Each row must have both invoice and contract PDF");
            return;
        }

        try {
            setSubmitting(true);

            const formData = new FormData();
            formData.append("excelFile", excelFile);

            const rowsMeta = previewRows.map((row) => ({
                key: row.key,
                requestNumber: row.requestNumber,
                rowNumber: row.rowNumber,
            }));

            formData.append(
                "rowsMeta",
                new Blob([JSON.stringify(rowsMeta)], { type: "application/json" })
            );

            previewRows.forEach((row) => {
                formData.append(`invoice_${row.key}`, row.invoiceFile);
                formData.append(`contract_${row.key}`, row.contractFile);
            });

            const result = await importCustomerTransactionExcel(formData);

            if (!result?.success) {
                message.error(result?.message || "Excel import failed");
                console.log("Customer Excel import result:", result);
                return;
            }

            message.success(result?.message || "Excel requests submitted successfully");

            setExcelFile(null);
            setPreviewRows([]);
        } catch (error) {
            message.error(error.message || "Failed to import Excel requests");
        } finally {
            setSubmitting(false);
        }
    };

    const removeRow = (key) => {
        setPreviewRows((prev) => prev.filter((row) => row.key !== key));
    };

    const columns = [
        { title: "Request", dataIndex: "requestNumber", width: 160 },
        { title: "Payer", dataIndex: "payer", width: 220 },
        {
            title: "Amount",
            key: "amount",
            width: 140,
            render: (_, row) => `${row.paymentAmount || "-"} ${row.currency || ""}`,
        },
        {
            title: "Invoice PDF",
            key: "invoiceFile",
            width: 220,
            render: (_, row) => (
                <PdfUploadField
                    compact
                    label=""
                    file={row.invoiceFile}
                    setFile={(file) => updateRowFile(row.key, "invoiceFile", file)}
                />
            ),
        },
        {
            title: "Contract PDF",
            key: "contractFile",
            width: 220,
            render: (_, row) => (
                <PdfUploadField
                    compact
                    label=""
                    file={row.contractFile}
                    setFile={(file) => updateRowFile(row.key, "contractFile", file)}
                />
            ),
        },
        {
            title: "Action",
            key: "action",
            width: 90,
            render: (_, row) => (
                <Button
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => removeRow(row.key)}
                />
            ),
        },
        {
            title: "Status",
            key: "ready",
            width: 120,
            render: (_, row) =>
                row.invoiceFile && row.contractFile ? (
                    <Tag color="green">Ready</Tag>
                ) : (
                    <Tag color="red">Missing PDFs</Tag>
                ),
        },
    ];

    return (
        <Space direction="vertical" style={{ width: "100%" }} size={16}>
            <Card title="Excel Import">
                <Dragger
                    accept=".xlsx,.xls"
                    beforeUpload={(file) => {
                        handlePreview(file);
                        return false;
                    }}
                    showUploadList={!!excelFile}
                    maxCount={1}
                >
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p>Upload Excel file</p>
                    <p>Supports both old and new schedule Excel structures</p>
                </Dragger>
            </Card>

            {previewRows.length > 0 ? (
                <Card
                    title="Preview"
                    extra={
                        <Button
                            type="primary"
                            icon={<UploadOutlined />}
                            disabled={!allRowsReady}
                            loading={submitting}
                            onClick={handleSubmit}
                        >
                            Submit All
                        </Button>
                    }
                >
                    {!allRowsReady ? (
                        <Alert
                            type="warning"
                            showIcon
                            message="Every row requires both invoice and contract PDF"
                            style={{ marginBottom: 16 }}
                        />
                    ) : null}

                    <Table
                        rowKey="key"
                        columns={columns}
                        dataSource={previewRows}
                        pagination={false}
                        scroll={{ x: 1100 }}
                    />
                </Card>
            ) : null}
        </Space>
    );
}